/**
 * Adyen Web v6 wrapper.
 *
 * Hides three migration concerns from the rest of the app:
 *
 * 1. **Named exports** — v6 uses `import { AdyenCheckout, SecuredFields }`
 *    instead of the v5 default `import AdyenCheckout`.
 * 2. **New constructor pattern** — v6 uses `new SecuredFields(checkout, config).mount(el)`
 *    instead of v5's `checkout.create('securedfields', config).mount(el)`.
 * 3. **CSS path** — v6 ships styles at `@adyen/adyen-web/styles/adyen.css`
 *    (v5 was `@adyen/adyen-web/dist/adyen.css`).
 *
 * Tests stub this whole module to avoid loading the real SDK under jsdom.
 *
 * Reference: https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&version=6.16.0
 */

import { AdyenCheckout, Card, CustomCard } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';

import type {
  AdyenAdvancedActions,
  AdyenAdvancedInitOptions,
  AdyenAuthoriseAdditionalData,
  AdyenAuthoriseSubmitData,
  AdyenCheckoutInitOptions,
  AdyenPaymentCompletedResult,
  AdyenSecuredFieldsState,
} from '../types/adyen';
import { ADYEN_CONFIG } from '../utils/constants';

/** Loosely-typed handle to the Adyen Checkout instance. Re-exported so callers can pass it around. */
export type AdyenCheckoutInstance = Awaited<ReturnType<typeof AdyenCheckout>>;

/** Handle returned by `mountSecuredFields` so the page can unmount on teardown. */
export interface SecuredFieldsHandle {
  unmount(): void;
  /** Trigger the actual Adyen Sessions API submission. Fires `onPaymentCompleted` with the real Adyen response (including `additionalData`). */
  submit(): void;
  /**
   * Force every encrypted field to surface its validation error, even fields
   * the user never focused. Mirrors what Adyen's built-in Pay button does on
   * click (v6 `showPayButton: true`). We render our own MUI button, so we must
   * call this ourselves when the shopper tries to submit with invalid fields.
   */
  showValidation(): void;
}

type ChallengeWindowSize = '01' | '02' | '03' | '04' | '05';

function resolveChallengeWindowSize(): ChallengeWindowSize {
  // Adyen recommends using "05" (full viewport) for compact/mobile layouts
  // to avoid horizontal clipping inside issuer challenge pages.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return '03';
  }

  const isCompactViewport =
    window.matchMedia('(max-width: 767px)').matches ||
    window.matchMedia('(max-height: 500px)').matches;

  return isCompactViewport ? '05' : '03';
}

/**
 * Build an Adyen Checkout instance with the sane defaults we use everywhere:
 *  - amount echo from the BFF session (Adyen v6 pulls this from session if not set)
 *  - F&P locale + no Pay button (we render our own MUI Pay button)
 *  - `onPaymentCompleted` / `onError` callbacks routed to caller
 */
export async function createAdyenCheckout(
  options: AdyenCheckoutInitOptions,
): Promise<AdyenCheckoutInstance> {
  return AdyenCheckout({
    session: options.session,
    clientKey: options.clientKey,
    environment: options.environment,
    locale: options.locale ?? ADYEN_CONFIG.LOCALE,
    countryCode: options.countryCode,
    // The custom-card pattern owns its own submit button, so suppress
    // Adyen's built-in Pay button to avoid double-render.
    showPayButton: false,
    onPaymentCompleted: (result) =>
      options.onPaymentCompleted(result as unknown as AdyenPaymentCompletedResult),
    // Adyen Web v6 splits terminal outcomes: success-ish result codes
    // (Authorised / Received / Pending / PresentToShopper) fire
    // `onPaymentCompleted`, while Refused / Cancelled / Error fire
    // `onPaymentFailed`. Both carry the same `{ resultCode, ... }` shape, so we
    // route them through the SAME caller handler — otherwise a Refused payment
    // would be silently dropped and never redirect to the status page.
    onPaymentFailed: (result) =>
      options.onPaymentCompleted(result as unknown as AdyenPaymentCompletedResult),
    onError: (error) => options.onError(error as unknown as Error),
    analytics: { enabled: false },
  });
}

/**
 * Build an Adyen Checkout instance for the **Advanced** (no-session) tokenize
 * flow. Unlike {@link createAdyenCheckout}, there is no `session`: submitting
 * the card fires `onSubmit` with the CSE-encrypted blob + `browserInfo`, which
 * the caller relays to our server (`/payments`) to learn the exact
 * `recurringDetailReference`. A 3DS2 challenge resolves via `onAdditionalDetails`.
 */
export async function createAdyenCheckoutAdvanced(
  options: AdyenAdvancedInitOptions,
): Promise<AdyenCheckoutInstance> {
  const toActions = (actions: unknown): AdyenAdvancedActions => {
    const maybe = actions as
      | {
          resolve?: (payload: Record<string, unknown>) => void;
          reject?: () => void;
        }
      | undefined;
    return {
      resolve: (payload: Record<string, unknown>) => {
        maybe?.resolve?.(payload);
      },
      reject: () => {
        maybe?.reject?.();
      },
    };
  };

  return AdyenCheckout({
    clientKey: options.clientKey,
    environment: options.environment,
    locale: options.locale ?? ADYEN_CONFIG.LOCALE,
    countryCode: options.countryCode,
    showPayButton: false,
    // Advanced flow: the component hands us the encrypted card + browserInfo
    // plus callback controls. The caller must resolve/reject these controls.
    onSubmit: (state, _component, actions) => {
      const data = ((state as { data?: unknown })?.data ?? {}) as {
        paymentMethod?: AdyenAuthoriseSubmitData['paymentMethod'];
        browserInfo?: AdyenAuthoriseSubmitData['browserInfo'];
      };
      options.onSubmit(
        {
          paymentMethod: data.paymentMethod ?? {},
          browserInfo: data.browserInfo,
        },
        toActions(actions),
      );
    },
    // After `handleAction` renders and resolves a 3DS2 challenge, the component
    // surfaces `state.data.details` (+ optional `paymentData`) here for the
    // caller to POST to `/authorise-details`.
    onAdditionalDetails: (state, _component, actions) => {
      const data = ((state as { data?: unknown })?.data ?? {}) as {
        details?: Record<string, string>;
        paymentData?: string;
      };
      const payload: AdyenAuthoriseAdditionalData = { details: data.details ?? {} };
      if (data.paymentData) payload.paymentData = data.paymentData;
      options.onAdditionalDetails(payload, toActions(actions));
    },
    onError: (error) => options.onError(error as unknown as Error),
    analytics: { enabled: false },
  });
}

export interface SecuredFieldsMountOptions {
  /** DOM element to render into. Caller MUST keep this stable across renders. */
  container: HTMLElement;
  /** Fired whenever the user types into one of the encrypted fields. */
  onChange: (state: AdyenSecuredFieldsState) => void;
  /** Fired on hard SDK errors (network, IFrame load failure, ...). */
  onError?: (error: Error) => void;
  /** Locale override. Defaults to the global one. */
  locale?: string;
}

/**
 * Mount the encrypted card-field iframes (number / expiry / CVC) into
 * the caller-controlled DOM container.
 *
 * F&P custom-card UX places these inside MUI labels with an external holder-name
 * `<input>` and the F&P palette — see `components/payment/PaymentForm.tsx`.
 * The caller is responsible for rendering the `data-cse="encryptedCardNumber"`
 * etc. placeholders Adyen looks for.
 */
export function mountSecuredFields(
  checkout: AdyenCheckoutInstance,
  options: SecuredFieldsMountOptions,
): SecuredFieldsHandle {
  const challengeWindowSize = resolveChallengeWindowSize();

  // Adyen v6 CustomCard surfaces the BIN-detected brand via `onBinLookup`,
  // NOT via the `onChange` state. We merge the latest detected brand into
  // every `onChange` emission so callers can read `state.brand` uniformly.
  let detectedBrand: string | undefined;
  let lastState: Record<string, unknown> | null = null;

  const emit = (): void => {
    if (!lastState) return;
    options.onChange({
      ...lastState,
      ...(detectedBrand ? { brand: detectedBrand } : {}),
    } as unknown as AdyenSecuredFieldsState);
  };

  const component = new CustomCard(checkout, {
    // ADYEN_CONFIG.SUPPORTED_BRANDS is a readonly tuple — spread into a fresh array.
    brands: [...ADYEN_CONFIG.SUPPORTED_BRANDS],
    challengeWindowSize,
    styles: ADYEN_CONFIG.STYLE_CONFIG,
    onChange: (state: unknown) => {
      lastState = state as Record<string, unknown>;
      emit();
    },
    onBinLookup: (binState: { detectedBrands?: string[]; isReset?: boolean }) => {
      if (binState.isReset) {
        detectedBrand = undefined;
      } else if (binState.detectedBrands && binState.detectedBrands.length > 0) {
        detectedBrand = binState.detectedBrands[0];
      }
      emit();
    },
    onError: (error: unknown) => {
      if (options.onError && error instanceof Error) options.onError(error);
    },
  });

  component.mount(options.container);

  return {
    unmount(): void {
      // v6 `Component#unmount` is best-effort — guard against double-unmount
      // (StrictMode + cleanup) by trapping any throw.
      try {
        component.unmount();
      } catch {
        /* noop */
      }
    },
    submit(): void {
      // Triggers the Adyen Sessions API flow: encrypted card data is sent to
      // Adyen and `onPaymentCompleted` fires with the real result (including
      // `additionalData['recurring.recurringDetailReference']`).
      (component as unknown as { submit(): void }).submit();
    },
    showValidation(): void {
      (component as unknown as { showValidation?(): void }).showValidation?.();
    },
  };
}

/** Handle for the Advanced-flow encrypted fields — adds 3DS `handleAction`. */
export interface AdvancedSecuredFieldsHandle extends SecuredFieldsHandle {
  /**
   * Render an Adyen 3DS2 `action` returned by our server's `/payments` call.
   * The resolved challenge surfaces via the checkout's `onAdditionalDetails`.
   */
  handleAction(action: Record<string, unknown>): void;
}

/**
 * Mount the encrypted card-field iframes for the **Advanced** tokenize flow.
 *
 * Identical UX to {@link mountSecuredFields}, but `submit()` triggers the
 * checkout's Advanced `onSubmit` (encrypted card + browserInfo) instead of the
 * Sessions API, and the returned handle exposes `handleAction` so the caller can
 * drive a 3DS2 challenge with the `action` our server returns.
 */
export function mountSecuredFieldsAdvanced(
  checkout: AdyenCheckoutInstance,
  options: SecuredFieldsMountOptions,
): AdvancedSecuredFieldsHandle {
  const challengeWindowSize = resolveChallengeWindowSize();

  let detectedBrand: string | undefined;
  let lastState: Record<string, unknown> | null = null;

  const emit = (): void => {
    if (!lastState) return;
    options.onChange({
      ...lastState,
      ...(detectedBrand ? { brand: detectedBrand } : {}),
    } as unknown as AdyenSecuredFieldsState);
  };

  const component = new CustomCard(checkout, {
    brands: [...ADYEN_CONFIG.SUPPORTED_BRANDS],
    challengeWindowSize,
    styles: ADYEN_CONFIG.STYLE_CONFIG,
    onChange: (state: unknown) => {
      lastState = state as Record<string, unknown>;
      emit();
    },
    onBinLookup: (binState: { detectedBrands?: string[]; isReset?: boolean }) => {
      if (binState.isReset) {
        detectedBrand = undefined;
      } else if (binState.detectedBrands && binState.detectedBrands.length > 0) {
        detectedBrand = binState.detectedBrands[0];
      }
      emit();
    },
    onError: (error: unknown) => {
      if (options.onError && error instanceof Error) options.onError(error);
    },
  });

  component.mount(options.container);

  return {
    unmount(): void {
      try {
        component.unmount();
      } catch {
        /* noop */
      }
    },
    submit(): void {
      // Advanced flow: validate + fire the checkout's `onSubmit` with the
      // encrypted card + browserInfo. Our server does the `/payments` call.
      (component as unknown as { submit(): void }).submit();
    },
    showValidation(): void {
      (component as unknown as { showValidation?(): void }).showValidation?.();
    },
    handleAction(action: Record<string, unknown>): void {
      (component as unknown as { handleAction(a: Record<string, unknown>): void }).handleAction(
        action,
      );
    },
  };
}

/** Options for the no-CVC one-click **Card** used to re-charge a saved ("stored") card. */
export interface StoredCardMountOptions {
  /** DOM element the (invisible) one-click component + any 3DS challenge render into. */
  container: HTMLElement;
  /**
   * Adyen vault token of the saved card (`storedPaymentMethodId`, a.k.a.
   * `recurringDetailReference`). Binds the one-click component to the exact card
   * so `submit()` authorises it during the 3DS fallback.
   */
  storedPaymentMethodId: string;
  /** Fired on hard SDK errors (network, IFrame load failure, ...). */
  onError?: (error: Error) => void;
}

/**
 * Mount a **no-CVC one-click Card** for a saved ("stored") card.
 *
 * The happy path never renders this — the vaulted card is charged server-side by
 * the BFF headless confirm (Adyen ContAuth `/payments`), so no security code is
 * ever collected in the browser. This component exists ONLY for the 3DS
 * fallback: when the issuer challenges the headless charge, the caller calls
 * `submit()`, the native 3DS2 flow renders into `container`, and the result
 * arrives via the checkout's `onPaymentCompleted`.
 *
 * `hideCVC: true` + `oneClick` + the checkout's `showPayButton: false` mean the
 * component renders nothing visible until a challenge appears. This REQUIRES the
 * Adyen merchant account to NOT enforce CVC for stored payments (Customer Area →
 * "Require CVC for stored cards" OFF), otherwise the no-CVC authorisation is
 * refused.
 *
 * Reference: https://docs.adyen.com/payment-methods/cards/web-component
 */
export function mountStoredCard(
  checkout: AdyenCheckoutInstance,
  options: StoredCardMountOptions,
): SecuredFieldsHandle {
  const challengeWindowSize = resolveChallengeWindowSize();

  const component = new Card(checkout, {
    storedPaymentMethodId: options.storedPaymentMethodId,
    isStoredPaymentMethod: true,
    oneClick: true,
    challengeWindowSize,
    // Never prompt for a security code — the account is configured to not require
    // CVC for stored payments, so the one-click authorisation needs no CVC.
    hideCVC: true,
    onError: (error: unknown) => {
      if (options.onError && error instanceof Error) options.onError(error);
    },
  });

  component.mount(options.container);

  return {
    unmount(): void {
      try {
        component.unmount();
      } catch {
        /* noop */
      }
    },
    submit(): void {
      // 3DS fallback: authorise the stored card via the Sessions API; the 3DS2
      // challenge (if any) renders into `container` and `onPaymentCompleted`
      // fires with the real pspReference + resultCode.
      (component as unknown as { submit(): void }).submit();
    },
    showValidation(): void {
      (component as unknown as { showValidation?(): void }).showValidation?.();
    },
  };
}
